import { matchPath, generatePath } from 'react-router-dom';
import { pipe, Option, Array as A } from 'effect';
import ROUTES_PATH from '@/router/routesPathConfig';

const PAY_MODULE_PREFIX = '/pay/';

export const isPayModulePath = (pathname: string): boolean => pathname.startsWith(PAY_MODULE_PREFIX);

type RedirectRule = {
  readonly pattern: string;
  readonly end?: boolean;
  readonly target: string;
};

// Order matters: the first matching rule wins.
const REDIRECT_RULES: ReadonlyArray<RedirectRule> = [
  // from AP to PAY
  { pattern: ROUTES_PATH.COMPANY_WORKSPACE.absolute, target: ROUTES_PATH.PAY_COMPANY.absolute },
  { pattern: ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute, target: ROUTES_PATH.PAY_COMPANY.absolute },
  { pattern: ROUTES_PATH.DOCUMENT.absolute, target: ROUTES_PATH.PAY_COMPANY.absolute },
  { pattern: ROUTES_PATH.COMPANY_SETTINGS.absolute, target: ROUTES_PATH.PAY_COMPANY_SETTINGS_PROVIDERS.absolute },
  // Pay has no bank feed page — every bank feed tab opens the Pay payments workspace (DPM-981)
  { pattern: ROUTES_PATH.COMPANY_BANK_FEED.absolute, end: false, target: ROUTES_PATH.PAY_COMPANY.absolute },
  // from PAY to AP
  {
    pattern: ROUTES_PATH.PAY_COMPANY.absolute,
    end: false,
    target: ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute,
  },
  { pattern: ROUTES_PATH.PAY_PAYMENT.absolute, target: ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute },
  { pattern: ROUTES_PATH.PAY_COMPANY_SETTINGS.absolute, target: ROUTES_PATH.COMPANY_SETTINGS_GENERAL_MAIN.absolute },
];

/**
 * Path the application switcher opens for the current page, or null when there is none.
 * Any AP company page without a specific counterpart falls back to the Pay payments workspace.
 */
export const getAppSwitchRedirectPath = (pathname: string, companyId?: string | null): string | null =>
  pipe(
    Option.fromNullable(companyId),
    Option.filter((id) => id.length > 0),
    Option.flatMap((id) =>
      pipe(
        REDIRECT_RULES,
        A.findFirst(({ pattern, end }) => matchPath({ path: pattern, end }, pathname) !== null),
        Option.map(({ target }) => target),
        Option.orElse(() =>
          isPayModulePath(pathname) ? Option.none() : Option.some(ROUTES_PATH.PAY_COMPANY.absolute),
        ),
        // Only the company id — never the full router params: on splat routes the inherited
        // '*' would leak into the destination.
        Option.map((target) => generatePath(target, { companyId: id })),
      ),
    ),
    Option.getOrNull,
  );
